import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { functions, db } from '../config/firebase';
import { FindCompaniesRequest, FindCompaniesResponse } from '../types/Company';
import { Company } from '../types/Company';
import { COLLECTION_NAMES } from '../utils/constants';
import { companyDedupeKey } from '../utils/companyUtils';

const findCompaniesFunction = httpsCallable<FindCompaniesRequest, FindCompaniesResponse>(
  functions,
  'find_companies',
  { timeout: 120000 }
);

// In-memory cache: userId -> Company[]
const companiesCache = new Map<string, Company[]>();

export const findCompaniesService = {
  async findCompanies(request: FindCompaniesRequest, userId?: string): Promise<FindCompaniesResponse> {
    const result = await findCompaniesFunction(request);
    // Invalidate cache so next loadCompanies reflects the new results
    if (userId) companiesCache.delete(userId);
    return result.data;
  },

  async loadCompanies(userId: string): Promise<Company[]> {
    if (companiesCache.has(userId)) {
      return companiesCache.get(userId)!;
    }

    const q = query(
      collection(db, COLLECTION_NAMES.COMPANIES),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
    );
    const snapshot = await getDocs(q);
    // De-duplicate by the company's normalized website (falling back to name)
    // so any duplicate documents do not surface as duplicate cards. Two
    // companies can share a name but not a primary domain. Results are ordered
    // newest-first, so the first occurrence is kept.
    const seen = new Set<string>();
    const companies = snapshot.docs.reduce<Company[]>((acc, doc) => {
      const data = doc.data();
      const companyName = data.companyName as string;
      if (!companyName) {
        return acc;
      }
      const website = data.website as string | undefined;
      const key = companyDedupeKey({ companyName, website });
      if (!key || seen.has(key)) {
        return acc;
      }
      seen.add(key);
      acc.push({
        companyName,
        website,
        rationale: data.rationale as string,
        searchId: data.searchId as string | undefined,
        sector: data.sector as string | undefined,
        industry: data.industry as string | undefined,
        geography: data.geography as string | undefined,
      });
      return acc;
    }, []);

    companiesCache.set(userId, companies);
    return companies;
  },

  updateCache(userId: string, companies: Company[]): void {
    companiesCache.set(userId, companies);
  },

  invalidateCache(userId: string): void {
    companiesCache.delete(userId);
  },
};
